import React, { useState, useEffect, useContext } from 'react'
import { BrowserRouter as Router, Route, Switch, useHistory } from 'react-router-dom'
import { reactPlugin } from "./AppInsights";
import { AppInsightsErrorBoundary, AppInsightsContext } from "@microsoft/applicationinsights-react-js";

import Styles from './App.module.css'
import Spinner from './components/Spinner/Spinner'
import Ie from './components/Ie'

import { RootStoreContext } from "./_stores/rootStore";
import Axios from './components/Axios'
import AuthorizeRoute from './api/api-authorization/AuthorizeRoute';
import ApiAuthorizationRoutes from './api/api-authorization/ApiAuthorizationRoutes';
import { ApplicationPaths } from './api/api-authorization/ApiAuthorizationConstants';
import Navbar from './components/Navbar/Navbar'
import { ExercisesContext } from './ExercisesContext'

//pages
import HcpDetails from './pages/HcpDetails/HcpDetails'
import HcoDetails from './pages/HcoDetails/HcoDetails'
import Home from './pages/Home/Home'
import Search from './pages/Search/Search'
import Xsearch from './pages/ExUsSearch/Xsearch'
import ExUsDetails from './pages/ExUsDetails/ExUsDetails'
import Admin from './pages/Admin/Admin'
import Targeting from './pages/Targeting/Targeting'
import TargetingV2 from './pages/TargetingV2/TargetingV2'
import useTargetingExperienceV2 from './hooks/useTargetingExperienceV2'
import { FEATURE_BRAND_REFRESH } from './features'
import './global.css'

import Workflow from './pages/Workflow/Workflow'

export default function App(props) {

    const rootStore = useContext(RootStoreContext)
    const { permissions } = rootStore.permissionsStore;
    const { user, token, isAuthenticated } = rootStore.userStore;
    const showTargetingExperienceV2 = useTargetingExperienceV2()
    const [darkMode, setDarkMode] = useState(() =>
        FEATURE_BRAND_REFRESH && localStorage.getItem('trinity_dark_mode') === 'true'
    )
    const [searchState, setSearchState] = useState(null)
    const [exercises, setExercises] = useState([])
    const [ie] = useState(!!window.document.documentMode)
    const [hashes, setHashes] = useState({ ctms: {}, spend: {}, crm: {} })
    const [loaded, setLoaded] = useState(false)

    const history = useHistory();

    useEffect(() => {
        if (!!isAuthenticated && !!user & !!token) {
            getTargetingExercises()
        }
    }, [loaded])

    useEffect(() => {
        if (FEATURE_BRAND_REFRESH) {
            document.body.classList.add('brand-refresh')
            if (darkMode) {
                document.body.classList.add('dark-mode')
            } else {
                document.body.classList.remove('dark-mode')
            }
        } else {
            document.body.classList.remove('brand-refresh')
            document.body.classList.remove('dark-mode')
        }
    }, [darkMode])

    const toggleDarkMode = () => {
        setDarkMode(prev => {
            const next = !prev
            localStorage.setItem('trinity_dark_mode', String(next))
            return next
        })
    }

    const hash = (profid, type, active) => {
        let ctms = { ...hashes.ctms }
        let spend = { ...hashes.spend }
        let crm = { ...hashes.crm }
        if (type === 0) {
            spend[profid] = active
        } else if (type === 1) {
            crm[profid] = active
        } else if (type === 2) {
            ctms[profid] = active
        }
        setHashes({ ctms, spend, crm })
    }

    const getTargetingExercises = () => {
        const { appAdmin, multiExercise } = permissions.targeting.global
        const { exercisePanel } = permissions.targeting.home

        const isAppAdmin = appAdmin.roles.includes(user.role)
        const isMultiExerciseAdmin =
            multiExercise.adminLevel.roles.includes(user.role) &&
            multiExercise.adminLevel.teams.includes(user.team)
        const isAdminUser = isAppAdmin || isMultiExerciseAdmin
        const isMultiExerciseUser =
            !isAdminUser && multiExercise.userLevel.teams.includes(user.team)

        const hasExerciseAccess =
            isAdminUser ||
            isMultiExerciseUser ||
            exercisePanel.include.roles.includes(user.role) ||
            (exercisePanel.include.teams.includes(user.team) &&
                !exercisePanel.exclude.roles.includes(user.role))

        if (hasExerciseAccess) {
            const { elevatedViewers, crossTeamExerciseAccess = {} } = permissions.targeting.global
            const isElevatedViewer = elevatedViewers.roles.includes(user.role)
            const crossAccess = isElevatedViewer ? crossTeamExerciseAccess : {}
            const teams = isAppAdmin
                ? undefined
                : (crossAccess[String(user.team)] ?? [String(user.team)])
            Axios('/api/v1/targeting/get-all-exercises', { teams, UserEmail: user.email }, (results) => {
                if (results) {
                    for (let i = 0; i < results.length; i++) {
                        results[i].END_DATE = results[i].END_DATE.slice(0, 10).replace(/-/g, '/').replace(/\//g, '-')
                        results[i].START_DATE = results[i].START_DATE.slice(0, 10).replace(/-/g, '/').replace(/\//g, '-')
                    }
                    if (!isAppAdmin) {
                        Axios('/api/v1/targeting/get-my-substitutions', { UserEmail: user.email }, (subs) => {
                            const subList = subs || []
                            const annotated = results.map(ex => {
                                if (subList.length > 0 && String(ex.TEAM) !== String(user.team)) {
                                    const match = subList.find(s =>
                                        s.VACANT_TERR_ID && String(s.VACANT_TERR_ID).startsWith(String(ex.TEAM))
                                    )
                                    return match ? { ...ex, vacantTerrID: match.VACANT_TERR_ID } : ex
                                }
                                else if (subList.length > 0 && String(ex.TEAM) === String(user.team)) {
                                    // Require the vacant territory to belong to the same team as the exercise
                                    // so a cross-team substitution (e.g. T13 record for a T21 rep) doesn't
                                    // accidentally override same-team exercises with the wrong territory.
                                    const match = subList.find(s =>
                                        s.VACANT_TERR_ID &&
                                        String(s.VACANT_TERR_ID).startsWith(String(ex.TEAM)) &&
                                        String(s.VACANT_TERR_ID) !== user.terr
                                    )
                                    return match ? { ...ex, vacantTerrID: match.VACANT_TERR_ID } : ex
                                }
                                return ex
                            })
                            setExercises({ ...exercises, targeting: annotated })
                            setLoaded(true)
                        })
                    } else {
                        setExercises({ ...exercises, targeting: results })
                        setLoaded(true)
                    }
                }
            })
        } else {
            setLoaded(true)
        }
    }

    const saveState = (newSearchState) => {
        setSearchState(newSearchState)
    }

    if (ie) {
        return <Ie />
    }

    const api_regex = /^\/api\/.*/
    const connect_regex = /^\/connect\/.*/
    const swagger_regex = /^\/swagger\/.*/
    // if using "/api/" in the pathname, don't use React Router
    if (api_regex.test(window.location.pathname) ||
        connect_regex.test(window.location.pathname) ||
        swagger_regex.test(window.location.pathname)
            ) {
        return <div /> // must return at least an empty div
    } else {
        if (!loaded && !isAuthenticated && !user && !token) {
            return <Spinner />
        } else {
            return (
                <AppInsightsErrorBoundary onError={() => <Spinner />} appInsights={reactPlugin}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                        <Router history={history}>
                            <ExercisesContext.Provider value={exercises}>
                            <div className={Styles.App}>
                                <div className={Styles.MainView}>
                                    <Switch>
                                        <Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />
                                        <AuthorizeRoute
                                            path='/'
                                            component={Navbar}
                                            darkMode={darkMode}
                                            toggleDarkMode={toggleDarkMode}
                                        />
                                    </Switch>
                                    <AuthorizeRoute
                                        path='/'
                                        exact
                                        component={Home}
                                    />
                                    <AuthorizeRoute
                                        path='/hcp'
                                        exact
                                        component={Search}
                                        saveState={saveState}
                                        searchState={searchState}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/xus'
                                        exact
                                        component={Xsearch}
                                        saveState={saveState}
                                        searchState={searchState}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/hcp/details/:PROF_ID'
                                        exact
                                        component={HcpDetails}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/hco'
                                        exact
                                        component={Search}
                                        saveState={saveState}
                                        searchState={searchState}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/xus/details/:PROF_ID'
                                        exact
                                        component={ExUsDetails}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/Workflow'
                                        user={user}
                                        component={Workflow}

                                    />
                                    <AuthorizeRoute
                                        path='/hco/details/:ORG_ID'
                                        exact
                                        component={HcoDetails}
                                        user={user}
                                        hash={hash}
                                        hashes={hashes}
                                    />
                                    <AuthorizeRoute
                                        path='/targeting'
                                        component={Targeting}
                                        user={user}
                                    />
                                    {showTargetingExperienceV2 && (
                                        <AuthorizeRoute
                                            path='/targeting-v2'
                                            component={TargetingV2}
                                            user={user}
                                        />
                                    )}
                                    <AuthorizeRoute path='/admin' exact component={Admin} user={user} />
                                </div>
                            </div>
                            </ExercisesContext.Provider>
                        </Router>
                    </AppInsightsContext.Provider>
                </AppInsightsErrorBoundary>
            )
        }
    }
}